import { useEffect, useState } from "react";
import { api, fmtEUR } from "@/lib/api";

/** Modal: ICAA auto-adjustment assistant. Shows AI proposals; user accepts/rejects each.
 *  onApply(moves) is called with the accepted moves array.
 */
export default function IcaaAdjustModal({ projectId, currentItems, currentTotal, onClose, onApply }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState({});  // idx -> true/false

  useEffect(() => {
    let alive = true;
    api.post(`/icaa/projects/${projectId}/ai/auto-adjust`, {}, { timeout: 90000 })
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && setError(e?.response?.data?.detail || e.message || "Error"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [projectId]);

  // Compute simulated chapter totals if all/accepted moves applied
  const simulate = (movesToApply) => {
    const items = currentItems.map((it) => ({ ...it }));
    const byCode = Object.fromEntries(items.map((it) => [it.code, it]));
    movesToApply.forEach((mv) => {
      const src = byCode[mv.source_code];
      if (src) {
        const curTotal = (src.quantity || 0) * (src.unit_price || 0);
        const newTotal = Math.max(0, curTotal - mv.amount_to_move);
        if ((src.quantity || 0) > 0) src.unit_price = newTotal / src.quantity;
        else src.unit_price = newTotal;
      }
      const tgt = byCode[mv.target_code];
      if (tgt) {
        const curTotal = (tgt.quantity || 0) * (tgt.unit_price || 0);
        const newTotal = curTotal + mv.amount_to_move;
        const qty = (tgt.quantity || 0) > 0 ? tgt.quantity : 1;
        tgt.quantity = qty;
        tgt.unit_price = newTotal / qty;
      }
    });
    const chs = {};
    let g = 0;
    items.forEach((it) => {
      const t = (it.quantity || 0) * (it.unit_price || 0);
      chs[it.chapter] = (chs[it.chapter] || 0) + t;
      g += t;
    });
    return { chs, g };
  };

  const moves = data?.moves || [];
  const acceptedMoves = moves.filter((_, i) => accepted[i] !== false);  // default accept
  const sim = simulate(acceptedMoves);
  const pct11 = sim.g > 0 ? ((sim.chs["11"] || 0) / sim.g) * 100 : 0;
  const pct12 = sim.g > 0 ? ((sim.chs["12"] || 0) / sim.g) * 100 : 0;
  const ok11 = pct11 <= 7.05;
  const ok12 = pct12 <= 20.05;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/70 p-4" onClick={onClose} data-testid="icaa-adjust-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-[#FAFAF6] shadow-2xl">
        <header className="flex items-center justify-between border-b border-stone-300/60 px-6 py-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#26775E]">Asistente · auto-ajuste ICAA</p>
            <h2 className="mt-0.5 font-serif text-2xl">Cumplir Cap.11 ≤ 7% · Cap.12 ≤ 20%</h2>
          </div>
          <button onClick={onClose} data-testid="icaa-modal-close"
                  className="rounded-full p-2 text-stone-500 hover:bg-stone-200">×</button>
        </header>

        <div className="flex flex-1 flex-col overflow-y-auto p-6">
          {loading && (
            <div className="py-16 text-center text-stone-500">
              Claude está analizando tu presupuesto…
            </div>
          )}
          {error && <p className="text-red-600">{error}</p>}

          {!loading && data && (
            <>
              <div className="mb-4 rounded-xl border border-stone-300/70 bg-white p-4 text-sm text-stone-700">
                <p className="font-serif text-base">Estrategia propuesta</p>
                <p className="mt-2 leading-relaxed">{data.reasoning}</p>
              </div>

              {/* Simulator */}
              <div className="mb-4 grid grid-cols-2 gap-3">
                <SimCard label="Cap. 11 — Gastos generales" actual={pct11} max={7} ok={ok11} testid="sim-cap-11" />
                <SimCard label="Cap. 12 — Comercial / financieros" actual={pct12} max={20} ok={ok12} testid="sim-cap-12" />
              </div>

              {moves.length === 0 ? (
                <p className="rounded-xl border border-emerald-300 bg-emerald-50 p-5 text-emerald-800">
                  ✓ No hay ajustes que proponer. El presupuesto ya cumple los límites o no se pueden mover partidas con justificación clara.
                </p>
              ) : (
                <div className="space-y-3" data-testid="moves-list">
                  {moves.map((mv, i) => {
                    const isAccepted = accepted[i] !== false;
                    return (
                      <article key={i} data-testid={`move-${i}`}
                               className={`rounded-xl border-2 p-4 transition ${
                                 isAccepted ? "border-[#26775E] bg-white" : "border-stone-300 bg-stone-100/60 opacity-60"
                               }`}>
                        <div className="flex items-start gap-4">
                          <input type="checkbox" checked={isAccepted}
                                 onChange={() => setAccepted({ ...accepted, [i]: !isAccepted })}
                                 data-testid={`move-check-${i}`}
                                 className="mt-1 h-5 w-5 cursor-pointer accent-[#26775E]" />
                          <div className="flex-1">
                            <div className="flex items-center gap-3 text-sm">
                              <span className="rounded bg-red-100 px-2 py-0.5 font-mono text-xs text-red-800">
                                {mv.source_code}
                              </span>
                              <span className="text-stone-500">{mv.source_description}</span>
                              <span className="text-stone-400">→</span>
                              <span className="rounded bg-emerald-100 px-2 py-0.5 font-mono text-xs text-emerald-800">
                                {mv.target_code}
                              </span>
                              <span className="text-stone-500">{mv.target_description}</span>
                              <span className="ml-auto font-mono text-lg tabular-nums text-[#26775E]">
                                {fmtEUR(mv.amount_to_move)}
                              </span>
                            </div>
                            <p className="mt-2 text-xs italic text-stone-600">{mv.justification}</p>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              <p className="mt-6 rounded bg-stone-100 p-3 text-[11px] text-stone-500">
                <strong>Aviso:</strong> los importes movidos requieren justificación documental real (contratos, partes de trabajo).
                Esta propuesta de Claude se basa en patrones habituales pero debe revisarla un productor/a o gestor/a antes de presentar al ICAA.
              </p>
            </>
          )}
        </div>

        {moves.length > 0 && (
          <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-stone-300/60 bg-white px-6 py-4">
            <div className="flex flex-col gap-0.5">
              <p className="text-xs uppercase tracking-widest text-stone-500">
                {acceptedMoves.length} de {moves.length} movimientos aceptados
              </p>
              {(!ok11 || !ok12) && acceptedMoves.length > 0 && (
                <p className="text-[11px] font-semibold text-red-700">
                  ⚠ Con los movimientos aceptados, el presupuesto aún no cumple los límites ICAA.
                  Acepta todos o ajusta manualmente después.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} data-testid="icaa-cancel-btn"
                      className="rounded-full border border-stone-400 px-5 py-2 text-sm uppercase tracking-widest text-stone-600 hover:bg-stone-100">
                Cancelar
              </button>
              <button onClick={() => onApply(acceptedMoves)}
                      disabled={acceptedMoves.length === 0}
                      data-testid="icaa-apply-btn"
                      className="rounded-full bg-[#26775E] px-6 py-2 text-sm uppercase tracking-widest text-white transition hover:bg-stone-900 disabled:opacity-40">
                Aplicar {acceptedMoves.length} movimiento{acceptedMoves.length !== 1 ? "s" : ""}
              </button>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}

const SimCard = ({ label, actual, max, ok, testid }) => (
  <div data-testid={testid}
       className={`rounded-xl border p-4 ${ok ? "border-emerald-400 bg-emerald-50" : "border-red-400 bg-red-50"}`}>
    <p className="text-[10px] uppercase tracking-widest text-stone-500">{label}</p>
    <p className={`mt-1 font-serif text-3xl ${ok ? "text-emerald-700" : "text-red-700"}`}>
      {actual.toFixed(2)}%
    </p>
    <p className="text-xs text-stone-500">
      {ok ? "✓" : "✗"} máximo permitido {max}%
    </p>
  </div>
);
